<template>
    <div class="roteirizacao-motoristas-params pa-3">
        <v-row dense>
            <v-col cols="12"  md="4">
                <v-text-field
                    :value="value.preJornadaMin"
                    type="number"
                    min="0"
                    :label="$t('routePlanning.drivers.params.preShift')"
                    :rules="nonNegativeRules"
                    suffix="min"
                    class="mr-3"
                    @input="update('preJornadaMin', $event)"
                />
            </v-col>
            <v-col cols="12"  md="4">
                <v-text-field
                    :value="value.posJornadaMin"
                    type="number"
                    min="0"
                    :label="$t('routePlanning.drivers.params.postShift')"
                    :rules="nonNegativeRules"
                    suffix="min"
                    class="mr-3"
                    @input="update('posJornadaMin', $event)"
                />
            </v-col>
            <v-col cols="12" md="4">
                <v-text-field
                    :value="tempoReusoVeiculos"
                    type="number"
                    min="0"
                    suffix="min"
                    :label="$t('routePlanning.fields.tempoReusoVeiculos')"
                    :rules="tempoReusoVeiculosRules"
                    @input="$emit('update:tempoReusoVeiculos', $event)"
                />
            </v-col>
            <v-col cols="12" md="3">
                <v-switch
                    :input-value="value.aplicarAlmoco !== false"
                    dense
                    inset
                    :label="$t('routePlanning.drivers.params.applyLunch')"
                    @change="update('aplicarAlmoco', !!$event)"
                />
            </v-col>
            <v-col cols="12" md="3" v-if="value.aplicarAlmoco">
                <v-text-field
                    :value="value.almocoMin"
                    type="number"
                    min="0"
                    :label="$t('routePlanning.drivers.params.lunch')"
                    :rules="nonNegativeRules"
                    suffix="min"
                    @input="update('almocoMin', $event)"
                />
            </v-col>
        </v-row>
        <div class="d-flex align-center flex-wrap mt-2 mb-3 pt-3">
            <span class="text-subtitle-2 mr-2">{{ $t('routePlanning.drivers.params.cyclesTitle') }}</span>
            <v-progress-circular v-if="cyclesLoading" indeterminate size="14" width="2" color="primary" class="mr-2" />
            <v-spacer />
            <v-btn x-small text color="primary" :disabled="cyclesLoading" @click="$emit('restore-cycles')">
                <v-icon x-small left>mdi-restore</v-icon>
                {{ $t('routePlanning.drivers.params.restoreCycles') }}
            </v-btn>
            <v-btn x-small text color="primary" @click="addCycle">
                <v-icon x-small left>mdi-plus</v-icon>
                {{ $t('routePlanning.drivers.params.addCycle') }}
            </v-btn>
        </div>
        <v-row v-for="(c, idx) in value.ciclos" :key="`ciclo-${idx}`" dense align="center">
            <v-col cols="12" md="3">
                <v-text-field
                    :value="c.ciclo"
                    dense
                    maxlength="50"
                    class="mt-3 mr-3"
                    :label="$t('routePlanning.drivers.params.cycleName')"
                    :rules="cycleNameRules(idx)"
                    @input="updateCycle(idx, 'ciclo', $event)"
                />
            </v-col>
            <v-col cols="4" md="2">
                <v-text-field
                    :value="c.inicio"
                    type="time"
                    dense
                    class="mt-2 mr-3"
                    :label="$t('routePlanning.drivers.params.cycleStart')"
                    :rules="timeRules"
                    @input="updateCycle(idx, 'inicio', $event)"
                />
            </v-col>
            <v-col cols="4" md="2">
                <v-text-field
                    :value="c.fim"
                    type="time"
                    dense
                    class="mt-2 mr-3"
                    :label="$t('routePlanning.drivers.params.cycleEnd')"
                    :rules="timeRules"
                    @input="updateCycle(idx, 'fim', $event)"
                />
            </v-col>
            <v-col cols="4" md="4">
                <v-switch
                    :input-value="c.trocaVeiculo !== false"
                    dense
                    inset
                    hide-details
                    class="mt-0"
                    :label="$t('routePlanning.drivers.params.vehicleSwitchBetweenCycles')"
                    @change="updateCycle(idx, 'trocaVeiculo', !!$event)"
                />
            </v-col>
            <v-col cols="1" class="text-center">
                <v-btn icon small :title="$t('routePlanning.drivers.params.removeCycle')" @click="removeCycle(idx)">
                    <v-icon  small color="red">mdi-delete</v-icon>
                </v-btn>
            </v-col>
        </v-row>

        <template v-if="modalRows.length">
            <div class="text-subtitle-2 mt-3 mb-1">{{ $t('routePlanning.drivers.params.shiftByTypeTitle') }}</div>
            <v-row dense>
                <v-col v-for="row in modalRows" :key="row.key" cols="12" md="3">
                    <v-text-field
                        :value="shiftValue(row)"
                        type="number"
                        min="1"
                        :label="row.label"
                        suffix="min"
                        class="mr-3"
                        :rules="optionalPositiveRules"
                        clearable
                        @input="updateShift(row.key, $event)"
                    />
                </v-col>
            </v-row>
        </template>
    </div>
</template>

<script>
import { JORNADA_LIQUIDA_PADRAO_MIN } from '@/utils/roteirizacaoMotoristas';

export default {
    name: 'RoteirizacaoMotoristasParams',
    props: {
        /** Parâmetros do sequenciamento (ver defaultDriverParams). */
        value: {
            type: Object,
            required: true,
        },
        /** Tipos de veículo da frota: `{ key, label, catalogMinutes }`. */
        modalRows: {
            type: Array,
            default: () => [],
        },
        /** De onde vieram os turnos: 'turnos_unidade' | 'turnos_gerais' | 'padrao' | 'historico' (ver DRIVER_CYCLES_SOURCE). */
        cyclesSource: {
            type: String,
            default: 'padrao',
        },
        cyclesLoading: {
            type: Boolean,
            default: false,
        },
        tempoReusoVeiculos: {
            type: [Number, String],
            default: 0,
        },
        tempoReusoVeiculosRules: {
            type: Array,
            default: () => [ (v) => (v !== '' && v != null && Number(v) >= 0) || 'Valor inválido.' ],
        },
    },
    data() {
        return {
            defaultShift: JORNADA_LIQUIDA_PADRAO_MIN,
            nonNegativeRules: [
                (v) => (v !== '' && v != null && Number(v) >= 0) || this.$t('routePlanning.drivers.params.invalidNumber'),
            ],
            optionalPositiveRules: [
                (v) => (v === '' || v == null || Number(v) > 0) || this.$t('routePlanning.drivers.params.invalidNumber'),
            ],
            timeRules: [
                (v) => /^\d{2}:\d{2}$/.test(String(v || '')) || this.$t('routePlanning.drivers.params.invalidTime'),
            ],
        };
    },
    computed: {
        cyclesSourceText() {
            const key = `routePlanning.drivers.params.cyclesSource.${this.cyclesSource}`;
            return this.$te(key) ? this.$t(key) : this.cyclesSource;
        },
    },
    methods: {
        cycleNameRules(idx) {
            return [
                (v) => !!String(v || '').trim() || this.$t('routePlanning.drivers.params.cycleNameRequired'),
                (v) => !this.value.ciclos.some((c, i) => i !== idx && String(c.ciclo || '').trim() === String(v || '').trim())
                    || this.$t('routePlanning.drivers.params.cycleNameDuplicated'),
            ];
        },
        addCycle() {
            const ciclos = this.value.ciclos.concat([{
                ciclo: `T${this.value.ciclos.length + 1}`, inicio: '', fim: '', trocaVeiculo: true,
            }]);
            this.emit({ ciclos });
        },
        removeCycle(idx) {
            this.emit({ ciclos: this.value.ciclos.filter((_, i) => i !== idx) });
        },
        emit(patch) {
            this.$emit('input', { ...this.value, ...patch });
        },
        update(key, raw) {
            this.emit({ [key]: raw });
        },
        updateCycle(idx, key, raw) {
            const ciclos = this.value.ciclos.map((c, i) => (i === idx ? { ...c, [key]: raw } : c));
            this.emit({ ciclos });
        },
        /** Jornada exibida: ajuste manual, senão a do cadastro do tipo, senão o padrão. */
        shiftValue(row) {
            const override = (this.value.jornadaPorTipo || {})[row.key];
            if (override != null && override !== '') return override;
            return row.catalogMinutes > 0 ? row.catalogMinutes : this.defaultShift;
        },
        updateShift(key, raw) {
            const jornadaPorTipo = { ...this.value.jornadaPorTipo };
            if (raw === '' || raw == null) delete jornadaPorTipo[key];
            else jornadaPorTipo[key] = raw;
            this.emit({ jornadaPorTipo });
        },

    },
};
</script>
